import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import * as mime from 'react-native-mime-types';
import fileDownload from 'js-file-download';
import { useNavigate, useSearchParams  } from 'react-router-dom';
import {
    Ban, Fullscreen as FullscreenIcon, Minimize, Search, Send, Smile, EllipsisVertical, Eye, UsersRound,
    RefreshCw, CheckCheck, Check, AlertCircle, Filter, X, Download, ArrowDown, Paperclip, MessageCircleMore,
    Reply as ReplyIcon, MailIcon, Phone, MapPin, Building2
} from 'lucide-react';
import { Avatar, Skeleton, Tooltip, Menu, MenuItem, Chip, Box } from '@mui/material';
import EmojiPicker from 'emoji-picker-react';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useWhatsappMessageFileUpload } from '@Hooks/useWhatsappMessageFileUpload';
import UserUnsubscribe from '@CommonFunctionalDisplay/UserUnsubscribe';
import useDebounce from '@Hooks/useDebounce';
import { useInView } from 'react-intersection-observer';
import ContactDetailsPage from '@CommonFunctionalDisplay/ContactDetailsPage';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import WhatsappSenderTemplatePreview from '@CommonRepresentationalDisplay/WhatsappSenderTemplatePreview';
import LabelClickUpMenu from '@CommonRepresentationalDisplay/LabelClickUpMenu';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/Views/RegistryLinkViewService';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import WhatsappChatsViewService from '@Helpers/URLHelpers/WeboutService/Views/WhatsappChatsViewService';
import LeadsWhatsappMessagingHistoryService from '@Helpers/URLHelpers/WeboutService/LeadsWhatsappMessagingHistoryService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsAppChatsView.scss';

const COLOR_LIST = [
    '#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0',
    '#9a60b0', '#6cb060', '#b0ae60', '#65412b', '#cc9963', '#60748b',
    '#744700', '#5f8fba', '#3e6073', '#8e6dc1', '#473785', '#9158a9',
    '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f',
    '#780448', '#003e23', '#108764', '#1a4c3d', '#11888b', '#1a95bd',
    '#0d6c7b', '#25b56e', '#006e3d',
];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 10;
const MSG_SIZE = 10;

const getInitials = (str) => {
    if (!str?.length) return '??';
    str = str.trim();
    const parts = str.split(' ');
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return str.substring(0, 2).toUpperCase();
};

const avatarColor = (str) =>
    COLOR_LIST[(str?.charCodeAt(0) ?? 0) % COLOR_LIST.length];

const formatShortTime = (ts) =>
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(ts);

const formatDateLabel = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / ONE_DAY_MS);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
};


const StatusBadge = ({ status }) => {
    switch (status) {
        case 'sent': return <span className="wc-status wc-status--sent"><Check size={13} /></span>;
        case 'delivered': return <span className="wc-status wc-status--delivered"><CheckCheck size={13} /></span>;
        case 'read': return <span className="wc-status wc-status--read"><CheckCheck size={13} /></span>;
        case 'failed':
        case 'error': return <span className="wc-status wc-status--failed"><AlertCircle size={13} /></span>;
        default: return null;
    }
};

const ReplyPreview = ({ findMsg, messagesList }) => {
    if (!findMsg) return null;
    const type = findMsg?.details?.type;
    return (
        <div className="wc-reply-preview">
            <span className="wc-reply-text">
                {
                    type === 'template' ?
                        <WhatsappSenderTemplatePreview template={findMsg.details} showBody={true} hideBackground/>
                        :
                        <MessageBody
                            details={findMsg.details}
                            messagesList={messagesList}
                            onPreview={null}
                            onDownload={null}
                            isReplyPreview={true}
                        />
                }
            </span>
        </div>
    );
};

const MessageBody = ({ details, messagesList, onPreview, onDownload }) => {

    const contextId = details?.context?.id;
    let findMsg = null;
    if(!!contextId) {
        findMsg = messagesList.find(m => m.messageId === contextId);
    }
    const Reply = !!findMsg ? <ReplyPreview findMsg={findMsg} messagesList={messagesList} /> : '';

    switch (details?.type) {
        case 'text':
            return <div>{Reply}<p className="wc-text">{details.text?.body}</p></div>;

        case 'interactive': {
            const iType = details.interactive?.type ?? 'button';
            if (iType === 'button_reply') {
                return (
                    <>{Reply}
                        <div className="wc-interactive-card">
                            <p className="wc-text">{details.interactive.button_reply.title}</p>
                        </div></>
                );
            }
            return (
                <>{Reply}
                    <div className="wc-interactive-card">
                        {details.interactive?.header?.type === 'text' && <div className="wc-ic-header">{details.interactive.header.text}</div>}
                        {details.interactive?.body && <div className="wc-ic-body">{details.interactive.body.text}</div>}
                        {details.interactive?.footer && <div className="wc-ic-footer">{details.interactive.footer.text}</div>}
                        {details.interactive?.action?.buttons && (
                            <div className="wc-ic-buttons">
                                {details.interactive.action.buttons.map((btn, i) => (
                                    <button key={i} className="wc-ic-btn">
                                        {btn?.type === 'reply' && <ReplyIcon size={16} />}
                                        {btn?.reply?.title}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div></>
            );
        }

        case 'image':
            return (
                <>{Reply}
                    <div className="wc-media wc-clickable"
                        onClick={() => onPreview('image', details.image.base64 || details.image.url)}>
                        <img src={details.image.base64 || details.image.url} alt="img" />
                        {details.image.caption && <p className="wc-caption">{details.image.caption}</p>}
                    </div></>
            );

        case 'video':
            return (
                <>{Reply}
                    <div className="wc-media">
                        <video controls src={details.video.base64 || details.video.url} />
                        {details.video.caption && <p className="wc-caption">{details.video.caption}</p>}
                    </div></>
            );

        case 'sticker':
            return (
                <>{Reply}
                    <img className="wc-sticker wc-clickable"
                        src={details.sticker.base64 || details.sticker.url}
                        onClick={() => onPreview('image', details.sticker.base64 || details.sticker.url)}
                        alt="sticker" /></>
            );

        case 'document':
            return (
                <>{Reply}
                    <div className="wc-document wc-clickable"
                        onClick={() => onDownload(details.document.url, details.document.filename)}>
                        <span>📄 {details.document.filename}</span>
                        <Download size={15} />
                    </div></>
            );

        case 'audio':
            return <>{Reply}<audio controls src={details.audio.base64 || details.audio.url} /></>;

        case 'button':
            return <div>{Reply}<p className="wc-text">{details?.button?.text}</p></div>;

        default:
            return <p className="wc-unsupported">Unsupported message type</p>;
    }
};

const MessageRow = ({ msg, messagesList, onPreview, onDownload }) => {
    const isMe = msg.direction === 'outbound';
    const isTemplate = msg?.agent === 'whatsapp';
    const reactions = msg.details?.reaction ?? {};
    const reactKeys = Object.keys(reactions);

    return (
        <div className={`wc-msg-row ${isMe ? 'wc-msg-row--me' : ''}`}>
            <div className={`wc-bubble ${isMe ? 'wc-bubble--me' : ''} ${isTemplate ? 'wc-bubble--tpl' : ''}`}>
                {isTemplate
                    ? <WhatsappSenderTemplatePreview template={msg.details} hideBackground/>
                    : <MessageBody details={msg.details} messagesList={messagesList} onPreview={onPreview} onDownload={onDownload} />
                }
                {reactKeys.length > 0 && (
                    <div className="wc-reactions">
                        {reactKeys.map((k, i) => (
                            <span key={i} className="wc-reaction-pill">{k} {reactions[k]?.length}</span>
                        ))}
                    </div>
                )}
                <div className="wc-meta">
                    <span className="wc-meta-time">{formatShortTime(msg.createTimeStamp)}</span>
                    {isMe && <StatusBadge status={msg.status} />}
                </div>
            </div>
        </div>
    );
};

const DateDivider = ({ ts }) => (
    <div className="wc-date-divider">
        <span>{formatDateLabel(ts)}</span>
    </div>
);

const ChatSkeleton = () => (
    <div className="wc-sk-list">
        {[...Array(5)].map((_, i) => (
            <div key={i} className="wc-sk-item">
                <Skeleton variant="circular" width={44} height={44} />
                <div className="wc-sk-text">
                    <Skeleton variant="text" width="60%" height={15} />
                    <Skeleton variant="text" width="40%" height={13} />
                </div>
            </div>
        ))}
    </div>
);

const MsgSkeleton = () => (
    <div className="wc-sk-msgs">
        {[...Array(6)].map((_, i) => (
            <div key={i} className={`wc-sk-msg-row ${i % 3 === 0 ? 'right' : ''}`}>
                <Skeleton variant="rounded" width={200} height={42} sx={{ borderRadius: '18px' }} />
            </div>
        ))}
    </div>
);

function WhatsAppChatsView() {

    const svcs = useRef({
        chats: new WhatsappChatsViewService(),
        msgs: new LeadsWhatsappMessagingHistoryService(),
        reg: new RegistryLinkViewService(),
        leads: new LeadsService(),
        card: new NameCardService(),
        urlEncryption: new UrlEncryptionService(),
        err: new ErrorHandler(),
    }).current;

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const handle = useFullScreenHandle();

    const nameCardId = searchParams.get('contact');

    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const { currentLeadsId, currentPhoneNumber } = useReduxLeads();
    const { validate, error } = useWhatsappMessageFileUpload();

    const [chatList, setChatList] = useState([]);
    const [showContact, setShowContact] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [listPage, setListPage] = useState(0);
    const [listHasMore, setListHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showUnread, setShowUnread] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showOpen, setShowOpen] = useState(false);
    const debouncedSearch = useDebounce(searchQuery, 500, true);

    const [selectedChat, setSelectedChat] = useState(null);
    const [messagesList, setMessagesList] = useState([]);
    const [msgLoading, setMsgLoading] = useState(false);
    const [msgPage, setMsgPage] = useState(0);
    const [msgHasMore, setMsgHasMore] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [newerMsgCount, setNewerMsgCount] = useState(0);

    const [showMenu, setShowMenu] = useState(false);
    const [anchorMenu, setAnchorMenu] =useState(null);

    const [message, setMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [cursor, setCursor] = useState('auto');
    const [preview, setPreview] = useState({ open: false, type: null, src: null });
    const [showScrollBtn, setShowScrollBtn] = useState(false);

    const chatPanelRef = useRef(null);
    const prevScrollHeight = useRef(0);
    const isInitialLoad = useRef(false);
    const inputRef = useRef(null);
    const emojiRef = useRef(null);
    const lastMsgTimeRef = useRef(0);
    const fileInputRef = useRef(null);

    const [listSentinelRef, listSentinelInView] = useInView({ threshold: 0.1 });
    const [msgTopSentinelRef, msgTopInView] = useInView({ threshold: 0.1, rootMargin: '60px' });

    const buildListFilter = useCallback((page) => ({
        whatsappChatsFilter: {
            searchQuery: debouncedSearch,
            projectIdList: [currentProjectId],
            phoneNumberIdList: [currentPhoneNumber?.id],
            ...(showUnread ? { sortBy: 'unreadCount', sortAsc: false, unreadCount: 1 } : {}),
            ...(showOpen ? { lastReceivedMessageTimeStampFrom: (new Date().getTime() - ONE_DAY_MS)  } : {}),
        },
        page,
        size: PAGE_SIZE,
    }), [currentProjectId, currentPhoneNumber, showUnread, showOpen, debouncedSearch]);

    useEffect(() => {
        resetAndLoadContacts();
        fetchUnreadCount();
    }, [currentPhoneNumber?.id, currentProjectId]);

    useEffect(() => {
        resetAndLoadContacts();
    }, [debouncedSearch, showUnread, showOpen]);

    useEffect(() => {
        if (messagesList.length) {
            lastMsgTimeRef.current =
                messagesList[messagesList.length - 1].createTimeStamp;
        }
    }, [messagesList]);

    useEffect(() => {
        if (listSentinelInView && listHasMore && !listLoading) {
            loadMoreContacts();
        }
    }, [listSentinelInView]);

    useEffect(() => {
        if (msgTopInView && msgHasMore && !loadingOlder && selectedChat && !isInitialLoad.current) {
            loadOlderMessages();
        }
    }, [msgTopInView]);

    useEffect(() => {
        const interval = setInterval(() => {
            loadNewerMsg();
        }, 10000);
        return () => clearInterval(interval);
    }, [selectedChat?.target]);

    useLayoutEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }, [message]);

    useEffect(() => {
        if (!nameCardId || chatList.length === 0) return;
        const decryptNameCard = svcs.urlEncryption.decryptData(nameCardId);
        const findChat = chatList.find(item => String(item.nameCardId) === String(decryptNameCard));
        if (findChat && selectedChat?.nameCardId !== findChat.nameCardId) {
            handleSelectChat(findChat);
        }
    }, [nameCardId, chatList]);

    const resetAndLoadContacts = async () => {
        setListLoading(true);
        setChatList([]);
        setListPage(0);
        setListHasMore(true);
        try {
            const res = await svcs.chats.getListFiltered(buildListFilter(0));
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                setChatList(data.conversationList ?? []);
                setListHasMore((data.totalPages ?? 0) > 1);
                setListPage(1);
            }
        } catch (err) { svcs.err.handleError(err); }
        setListLoading(false);
    };

    const loadMoreContacts = async () => {
        if (listLoading) return;
        setListLoading(true);
        try {
            const res = await svcs.chats.getListFiltered(buildListFilter(listPage));
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                const next = data.conversationList ?? [];
                setChatList(prev => {
                    const ids = new Set(prev.map(c => c.lastMessageId));
                    return [...prev, ...next.filter(c => !ids.has(c.lastMessageId))];
                });
                setListHasMore(listPage + 1 < (data.totalPages ?? 0));
                setListPage(p => p + 1);
            }
        } catch (err) { svcs.err.handleError(err); }
        setListLoading(false);
    };

    const fetchUnreadCount = async () => {
        try {
            const res = await svcs.chats.getUnreadCountByPhoneNumberIdAndProjectId(
                currentPhoneNumber?.id, currentProjectId
            );
            if (res.status < 400) setUnreadCount(JSON.parse(res.body));
        } catch (err) { svcs.err.handleError(err); }
    };

    const fetchChat = async (chat) => {
        isInitialLoad.current = true;
        setMsgLoading(true);
        setMessagesList([]);
        try {
            const body = {
                page: 0, size: MSG_SIZE,
                leadsId: currentLeadsId,
                phoneNumberId: currentPhoneNumber?.id,
                target: chat.target,
                projectId: currentProjectId,
            };
            const res = await svcs.msgs.getListFiltered(body);
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                const list = (data.chatHistoryList ?? [])
                    .map(m => ({ ...m, details: JSON.parse(m.details) }))
                    .reverse();
                setMessagesList(list);
                setMsgPage(1);
                setMsgHasMore(list.length === MSG_SIZE);
                await fetchNameCard(chat.nameCardId);
                fetchUnreadCount();
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scrollToBottomInstant();
                        isInitialLoad.current = false;
                    });
                });
            }
        } catch (err) { svcs.err.handleError(err); }
        setMsgLoading(false);
    };

    const loadNewerMsg = async () => {
        try {
            if (!selectedChat || isInitialLoad.current) return;

            if (!selectedChat || isInitialLoad.current) return;
            const lastMsgTime = lastMsgTimeRef.current;

            if (lastMsgTime) {
                const body = {
                    page: 0, size: MSG_SIZE,
                    leadsId: currentLeadsId,
                    phoneNumberId: currentPhoneNumber?.id,
                    target: selectedChat.target,
                    projectId: currentProjectId,
                    lastMsgTimeStamp: lastMsgTime
                };
                const res = await svcs.msgs.getListFiltered(body);
                if (res.status < 400) {
                    const data = JSON.parse(res.body);
                    const list = (data.chatHistoryList ?? [])
                        .map(m => ({ ...m, details: JSON.parse(m.details) }))
                        .reverse();
                    if (list.length !== 0) {
                        setMessagesList(prev => [...prev, ...list]);
                        setNewerMsgCount(prev => (prev || 0) + list.length);
                        setShowScrollBtn(true);
                    }
                }
            }
        } catch (err) {
            svcs.err.handleError(err);
        }
    }

    const loadOlderMessages = async () => {
        if (!selectedChat || loadingOlder) return;
        setLoadingOlder(true);
        prevScrollHeight.current = chatPanelRef.current?.scrollHeight ?? 0;
        try {
            const body = {
                page: msgPage, size: MSG_SIZE,
                leadsId: currentLeadsId,
                phoneNumberId: currentPhoneNumber?.id,
                target: selectedChat.target,
                projectId: currentProjectId,
            };
            const res = await svcs.msgs.getListFiltered(body);
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                const older = (data.chatHistoryList ?? [])
                    .map(m => ({ ...m, details: JSON.parse(m.details) }))
                    .reverse();
                setMessagesList(prev => [...older, ...prev]);
                setMsgPage(p => p + 1);
                setMsgHasMore(older.length === MSG_SIZE);
            }
        } catch (err) { svcs.err.handleError(err); }
        setLoadingOlder(false);
    };

    useEffect(() => {
        if (!loadingOlder && prevScrollHeight.current && chatPanelRef.current) {
            const diff = chatPanelRef.current.scrollHeight - prevScrollHeight.current;
            chatPanelRef.current.scrollTop += diff;
            prevScrollHeight.current = 0;
        }
    }, [loadingOlder]);

    const fetchNameCard = async (id) => {
        try {
            const res = await svcs.card.getByIdFetch(id);
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                setSelectedChat(prev => ({ ...prev, chatWindowOpen: data.chatWindowOpen }));
            }
        } catch (err) { svcs.err.handleError(err); }
    };

    const handleSelectChat = (chat) => {
        let id = svcs.urlEncryption.encryptData(chat?.nameCardId);
        setSearchParams({ contact: id });
        if (selectedChat?.lastMessageId === chat.lastMessageId) return;
        setSelectedChat(chat);
        setShowScrollBtn(false);
        setNewerMsgCount(0);
        fetchChat(chat);
        setChatList(prev =>
            prev.map(c => c.lastMessageId === chat.lastMessageId ? { ...c, unreadCount: 0 } : c)
        );
    };

    const handleFileSelect = async(e)=> {
        setCursor('wait');
        const file = e.target.files[0];
        if (!!file) {
            const result = await validate(file);
            if(result.isValid) {
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    projectId: currentProjectId,
                    type: getSimplifiedFileType(file)
                }
                setSelectedFile(newFileObj);
            } else {
                SnackbarManager.show(result.error, 'error');
            }
        }
        setCursor('auto');
    };

    const getSimplifiedFileType = (file) => {
        const mimeType = file.type;
        const fileName = file.name.toLowerCase();
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(fileName)) return 'image';
        if (/\.(mp4|webm|ogg|mov|mkv)$/.test(fileName)) return 'video';
        return 'document';
    }

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleEmojiClick = (emojiData) => {
        setMessage(prev => prev + emojiData.emoji);
    };

    const handleSend = async () => {
        if (!message.trim() && !selectedFile) return;
        const bodyObj = {
            message,
            leadsId: currentLeadsId,
            sendToPhoneNumber: selectedChat.target,
            fromPhoneNumberId: currentPhoneNumber?.id,
            projectId: currentProjectId,
            senderId: activeUserId,
        };
        if(!!selectedFile) {
            bodyObj.file = selectedFile;
        }
        setCursor('wait');
        try {
            const res = await svcs.msgs.sendWhatsappApiMsg(bodyObj);
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                setMessagesList(prev => [...prev, { ...data, details: JSON.parse(data.details) }]);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => scrollToBottomInstant());
                });
                setSelectedFile(null);
                setMessage('');
                inputRef.current.style.height = 'auto';
                setShowEmojiPicker(false);
                if(!!bodyObj.file) {
                    await fetchChat(selectedChat);
                }
            } else {
                SnackbarManager.show(res.message, 'error');
            }
        } catch (err) {
            svcs.err.handleError(err);
            SnackbarManager.show('Failed to send', 'error');
        }
        setCursor('auto');
    };

    const toggleChatWindow = async () => {
        setCursor('wait');
        try {
            const res = await svcs.card.toggleChatWindow({ id: selectedChat.nameCardId });
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                setSelectedChat(prev => ({ ...prev, chatWindowOpen: data.chatWindowOpen }));
                SnackbarManager.show('Chat window updated', 'success');
            }
        } catch (err) { svcs.err.handleError(err); }
        setCursor('auto');
    };

    const scrollToBottomInstant = () => {
        const el = chatPanelRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
        setShowScrollBtn(false);
    };

    const scrollToBottomSmooth = () => {
        const el = chatPanelRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        setShowScrollBtn(false);
    };

    const handleChatScroll = () => {
        const el = chatPanelRef.current;
        if (!el) return;
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShowScrollBtn(distFromBottom > 120);
    };

    const openPreview = (type, src) => setPreview({ open: true, type, src });
    const closePreview = () => setPreview({ open: false, type: null, src: null });

    const downloadFile = (base64String, fileName) => {
        if (!base64String) return;
        const [header] = base64String.split(',');
        const mime = header.match(/data:(.*?);base64/)[1];
        const ext = mime.split('/')[1];
        if (!fileName) fileName = `file_${Date.now()}.${ext}`;
        fileDownload(dataURLtoFile(base64String), fileName);
    };

    const dataURLtoFile = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], { type: mime });
    };

    const messagesWithDividers = (() => {
        const items = [];
        let lastDate = null;
        messagesList.forEach((msg) => {
            const d = new Date(msg.createTimeStamp).toDateString();
            if (d !== lastDate) {
                items.push({ isDivider: true, ts: msg.createTimeStamp, key: `div-${d}` });
                lastDate = d;
            }
            items.push({ isDivider: false, msg, key: msg.id ?? msg.messageId ?? Math.random() });
        });
        return items;
    })();

    const handleMenuPopup = (event) => {
        if(!showMenu){
            if(!!event) {
                setAnchorMenu(event.currentTarget);
                setShowMenu(true);
            }
        } else {
            setAnchorMenu(null);
            setShowMenu(false);
        }
    }

    const MenuItems = ({icon, title, onClick, color})=> {
        return (
            <MenuItem onClick={onClick}
               sx={{
                   borderRadius: '6px',
                   margin: '4px 8px',
                   fontWeight: 400,
                   '&:hover': {
                       fontWeight: 600,
                       backgroundColor: 'rgba(0, 0, 0, 0.04)',
                       cursor: 'pointer',
                           '&:hover .item-menu': {
                             color: color,
                           },
                   },
               }}>
                <div className="flex-row-center-0p5rem-gap item-menu">
                    {icon}
                    {title}
                </div>
            </MenuItem>
       )
    }

    const handleCreateLead = async (e) => {
        let sanitizePhone = selectedChat.target;
        sanitizePhone = sanitizePhone.length > 10 ? sanitizePhone.slice(2, sanitizePhone.length) : sanitizePhone;
        const newLead = {
            name: selectedChat?.userName,
            companyName: '',
            email: '',
            contactNumber: sanitizePhone,
            address: '',
            status: 'newLeads',
            source: 'whatsapp',
            query: '',
            statusUpdate: '',
            details: '{}',
            comments: '[]',
            reporterUserId: activeUserId,
            projectId: currentProjectId
        };
        try {
            let response = await svcs.leads.createLeads(newLead);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                handleMenuPopup();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            svcs.err.handleError(err);
        }
    };

    const toggleContactView = ()=> setShowContact(!showContact);

    const contactLabel = (chat) => chat?.userName || chat?.target || '';

    return (
            <div className="wc-root" style={{ cursor }}>
                <div className="wc-sidebar">
                    <div className="wc-sidebar-header">
                        <div className="wc-sidebar-title-wrap">
                            <h2 className="wc-sidebar-title">Chats</h2>
                            {unreadCount > 0 && <span className="wc-badge">{unreadCount}</span>}
                        </div>
                        <div className="wc-sidebar-actions">
                            <Tooltip title="Refresh" placement="top">
                                <button className="wc-icon-btn" onClick={resetAndLoadContacts}>
                                    <RefreshCw size={15} />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="wc-search-wrap">
                        <Search size={14} className="wc-search-icon" />
                        <input
                            className="wc-search-input"
                            type="text"
                            placeholder="Search chats..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="wc-search-clear" onClick={() => setSearchQuery('')}>
                                <X size={13} />
                            </button>
                        )}
                    </div>

                    <div className="wc-filter-row">
                        <button
                            className={`wc-filter-btn ${showUnread ? 'active' : ''}`}
                            onClick={() => setShowUnread(v => !v)}
                        >
                            <Filter size={13} /> Unread
                        </button>
                        <button
                            className={`wc-filter-btn ${showOpen ? 'active' : ''}`}
                            onClick={() => setShowOpen(v => !v)}
                        >
                            <MessageCircleMore size={13} /> Open
                        </button>
                    </div>

                    <div className="wc-contacts-scroll">
                        {listLoading && chatList.length === 0 ? (
                            <ChatSkeleton />
                        ) : chatList.length === 0 ? (
                            <div className="wc-empty"><p>No chats found</p></div>
                        ) : (
                            <>
                                {chatList.map((chat) => {
                                    const name = contactLabel(chat);
                                    const isActive = selectedChat?.lastMessageId === chat.lastMessageId;
                                    const isOpen = (Date.now() - chat.lastReceivedMessageTimeStamp) <= ONE_DAY_MS;
                                    return (
                                        <div
                                            key={chat.lastMessageId}
                                            className={`wc-contact-item ${isActive ? 'active' : ''}`}
                                            onClick={() => handleSelectChat(chat)}
                                        >
                                            <Avatar sx={{
                                                bgcolor: avatarColor(name) + ' !important',
                                                width: 44, height: 44, fontSize: 17, flexShrink: 0,
                                            }}>
                                                {getInitials(name)}
                                            </Avatar>
                                            <div className="wc-contact-info">
                                                <div className="wc-contact-row">
                                                    <span className="wc-contact-name">{name}</span>
                                                    <span className="wc-contact-time">{formatShortTime(chat.lastMessageTimeStamp)}</span>
                                                </div>
                                                <div className="wc-contact-row">
                                                    {
                                                        !!chat.lastMessage &&
                                                            <span className='wc-contact-t2'>
                                                                {chat?.lastMessage}
                                                            </span>
                                                    }
                                                    {isOpen && (
                                                        <span className="wc-open-badge">
                                                            <span className="wc-dot" />Open
                                                        </span>
                                                    )}
                                                    {chat.unreadCount > 0 && (
                                                        <span className="wc-unread-pill">{chat.unreadCount}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div ref={listSentinelRef} className="wc-sentinel">
                                    {listLoading && (
                                        <div className="wc-sk-list">
                                            {[...Array(2)].map((_, i) => (
                                                <div key={i} className="wc-sk-item">
                                                    <Skeleton variant="circular" width={44} height={44} />
                                                    <div className="wc-sk-text">
                                                        <Skeleton variant="text" width="60%" height={15} />
                                                        <Skeleton variant="text" width="40%" height={13} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {!selectedChat ? (
                    <div className="wc-panel wc-panel--empty"></div>
                ) : (
                    <div className="wc-panel">
                        <div className="wc-chat-header">
                            <Avatar sx={{
                                bgcolor: avatarColor(contactLabel(selectedChat)) + ' !important',
                                width: 40, height: 40, fontSize: 16, flexShrink: 0,
                            }}>
                                {getInitials(contactLabel(selectedChat))}
                            </Avatar>
                            <div className="wc-chat-header-info" onClick={toggleContactView}>
                                <span className="wc-chat-name">{contactLabel(selectedChat)}</span>
                                <span className="wc-chat-number">{selectedChat.target}</span>
                            </div>
                            <div className="wc-chat-header-actions">
                                <UserUnsubscribe id={selectedChat?.nameCardId}/>
                                <span className={`wc-auto-badge ${!selectedChat.chatWindowOpen ? 'on' : 'off'}`}>
                                    {!selectedChat.chatWindowOpen ? 'Auto Reply Active' : 'Auto Reply Off'}
                                </span>
                                {selectedChat.chatWindowOpen && (
                                    <Tooltip title="End conversation" placement="top">
                                        <button className="wc-icon-btn wc-icon-btn--danger" onClick={toggleChatWindow}>
                                            <Ban size={15} />
                                        </button>
                                    </Tooltip>
                                )}
                                <Tooltip title="Refresh messages" placement="top">
                                    <button className="wc-icon-btn" onClick={() => fetchChat(selectedChat)}>
                                        <RefreshCw size={15} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                        <div
                            className="wc-messages"
                            ref={chatPanelRef}
                            onScroll={handleChatScroll}>
                            <div ref={msgTopSentinelRef} className="wc-top-sentinel">
                                {loadingOlder && (
                                    <div className="wc-loading-older">
                                        <RefreshCw size={13} className="wc-spin" /> Loading older messages…
                                    </div>
                                )}
                            </div>
                            {msgLoading ? (
                                <MsgSkeleton />
                            ) : (
                                messagesWithDividers.map(item =>
                                    item.isDivider
                                        ? <DateDivider key={item.key} ts={item.ts} />
                                        : <MessageRow
                                            key={item.key}
                                            msg={item.msg}
                                            messagesList={messagesList}
                                            onPreview={openPreview}
                                            onDownload={downloadFile}
                                        />
                                )
                            )}
                        </div>
                        {showScrollBtn && (
                            <button
                                className="wc-scroll-fab"
                                onClick={() => {
                                    scrollToBottomSmooth();
                                    setNewerMsgCount(0);
                                }}
                            >
                                <ArrowDown size={16} />
                                {newerMsgCount > 0 && (
                                    <span className="wc-scroll-fab__count">
                                        {newerMsgCount}
                                    </span>
                                )}
                            </button>
                        )}
                        <div className="wc-composer">
                            {selectedFile && (
                                <div className="wc-file-preview">
                                    <div className="wc-file-preview__content">
                                        {selectedFile?.fileType?.startsWith('video/') ?
                                            <video controls width="220" height='100px' style={{borderRadius:8}}>
                                                <source src={selectedFile?.file} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                            :
                                                selectedFile?.fileType?.startsWith('image/') ?
                                                    <img
                                                        src={selectedFile?.file}
                                                        alt=""
                                                        className="wc-file-preview__image"
                                                    />
                                                :
                                                    <div className="wc-file-preview__document">
                                                        📄 {selectedFile?.fileName}
                                                    </div>
                                        }
                                    </div>
                                    <button
                                        className="wc-file-preview__remove"
                                        onClick={() => setSelectedFile(null)}>
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                            <div className="wc-input-row">
                                <div className='wc-input-row-top'>
                                    <div
                                        className="wc-emoji-container"
                                        ref={emojiRef}>
                                        <button
                                            className="wc-icon-btn"
                                            onClick={() =>
                                                setShowEmojiPicker(prev => !prev)
                                            }>
                                            <Smile size={22} />
                                        </button>
                                        {showEmojiPicker && (
                                            <div className="wc-emoji-picker">
                                                <EmojiPicker
                                                    onEmojiClick={handleEmojiClick}
                                                    lazyLoadEmojis
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className="wc-icon-btn"
                                        onClick={() => fileInputRef.current?.click()} >
                                        <Paperclip size={22} />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        hidden
                                        onChange={handleFileSelect}
                                    />
                                    <textarea
                                        ref={inputRef}
                                        className="wc-input"
                                        rows={1}
                                        placeholder="Type a message"
                                        value={message}
                                        onChange={(e)=>  setMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                if(cursor !== 'wait') {
                                                    handleSend();
                                                }
                                            }
                                        }}
                                    />
                                    <button
                                        className={`wc-send-btn ${
                                            message.trim() || selectedFile ? 'active' : ''
                                        }`}
                                        disabled={!message.trim() && !selectedFile || cursor === 'wait'}
                                        onClick={handleSend}>
                                        <Send size={18} />
                                    </button>
                                </div>
                                <div className="wc-composer-hint">
                                    <span>
                                        Press{' '}
                                        <kbd>
                                            Enter
                                        </kbd>{' '}
                                        to send
                                    </span>

                                    <span>
                                        <kbd>
                                            Shift +
                                            Enter
                                        </kbd>{' '}
                                        for new line
                                    </span>
                                </div>
                            </div>
                        </div>
                        {showMenu && (
                            <Menu
                                open={showMenu}
                                onClose={handleMenuPopup}
                                anchorEl={anchorMenu}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                PaperProps={{
                                    elevation: 8,
                                    sx: {
                                        minWidth: 200,
                                        borderRadius: '8px',
                                        mt: 1,
                                    }
                                }}>
                                <MenuItems icon={<Eye size={16}/>} color='#2784b9' title='View' onClick={toggleContactView}/>
                                <MenuItems icon={<UsersRound size={16}/>} color='#2784b9' title='Convert to leads' onClick={handleCreateLead}/>
                            </Menu>
                        )}
                    </div>
                )}

                {preview.open && (
                    <div className="wc-overlay" onClick={closePreview}>
                        <div className="wc-overlay-box" onClick={e => e.stopPropagation()}>
                            {preview.type === 'image' && <img src={preview.src} alt="preview" />}
                            {preview.type === 'video' && <video src={preview.src} controls autoPlay />}
                            <div className="wc-overlay-actions">
                                <button className="wc-overlay-btn" onClick={() => downloadFile(preview.src)}>
                                    <Download size={18} />
                                </button>
                                <button className="wc-overlay-btn" onClick={closePreview}>
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {
                    showContact &&
                        <ContactDetailsPage
                            reloadData={()=> resetAndLoadContacts()}
                            nameCardId={selectedChat?.nameCardId}
                            onClose={toggleContactView}/>
                }
            </div>
    );
}

export default WhatsAppChatsView;